import {
  BotPattern,
  botsTouched,
  confirmationFor,
  isVerifiedSearchEngine,
  roleAtLeast,
  BOT_CATALOG,
  type BotCatalogEntry,
  type ConfigScopeRef,
  type ServerGroupRef,
  type WebRuleChange,
} from '@bartizo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { needsReauth } from '../api/client.js';
import { changeWebRules } from '../api/traffic.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { CATEGORY_LABELS } from '../lib/traffic.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { SelectField, TextField } from '../ui/Field.js';
import { ConfirmDialog } from './ConfirmDialog.js';

/**
 * Block, rate-limit or allow a bot from the Traffic page (knowledge doc section
 * 12, "Web traffic controls"; P6.6).
 *
 * A rule is a setting, so the dialog asks where it applies — this server, its
 * group, or every server — and the API changes that layer by one entry. Section
 * 12's Medium row is this dialog; its High row, blocking or rate-limiting a
 * verified search engine, needs an admin, a fresh password re-check and the
 * engine's name typed, and the dialog says so before anything is sent.
 */

export type BotRuleAction = 'block' | 'rate_limit' | 'allow';

export interface BotRuleServer {
  id: string;
  name: string;
  group: ServerGroupRef | null;
}

export interface BotRuleDialogProps {
  server: BotRuleServer;
  bot: BotCatalogEntry;
  action: BotRuleAction;
  onClose: () => void;
}

type Scope = 'server' | 'group' | 'global';

const TITLES: Readonly<Record<BotRuleAction, string>> = {
  block: 'Block a bot',
  rate_limit: 'Rate-limit a bot',
  allow: 'Always allow a bot',
};

export function BotRuleDialog({ server, bot, action, onClose }: BotRuleDialogProps): ReactNode {
  const session = useCurrentUser();
  const isAdmin = roleAtLeast(session.user.role, 'admin');
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<Scope>('server');
  const [match, setMatch] = useState<'bot' | 'category'>('bot');
  const [perMinute, setPerMinute] = useState('60');
  const [burst, setBurst] = useState('5');
  const [error, setError] = useState<string | null>(null);

  const ref: ConfigScopeRef =
    scope === 'global'
      ? { scope: 'global' }
      : scope === 'group' && server.group !== null
        ? { scope: 'group', id: server.group.id }
        : { scope: 'server', id: server.id };

  const change: WebRuleChange =
    action === 'block'
      ? { kind: 'block', bot: bot.id }
      : action === 'allow'
        ? { kind: 'allow', bot: bot.id }
        : {
            kind: 'rate_limit',
            rule: {
              match: match === 'bot' ? `bot:${bot.id}` : `category:${bot.category}`,
              per_minute: Number(perMinute),
              burst: Number(burst),
            },
          };

  // Section 12's High row: a verified search engine newly blocked or limited.
  const reachesSearch =
    action !== 'allow' &&
    (isVerifiedSearchEngine(bot) ||
      (action === 'rate_limit' && match === 'category' && bot.category === 'search'));
  const phrase = confirmationFor(change, BOT_CATALOG);

  const save = useMutation({
    mutationFn: async () => {
      if (reachesSearch && !(await ensureReauthenticated())) {
        throw new Error('That needs your password again first.');
      }
      return changeWebRules({ scope: ref, change, ...(reachesSearch ? { confirm: phrase } : {}) });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['web-rules', server.id] });
      onClose();
    },
    onError: (caught: unknown) => {
      setError(
        needsReauth(caught)
          ? 'That needed a fresh password check. Try again.'
          : caught instanceof Error
            ? caught.message
            : 'Could not change the rules.',
      );
    },
  });

  if (reachesSearch && !isAdmin) {
    return (
      <Dialog title={TITLES[action]} description={bot.name} onClose={onClose}>
        <div className="stack">
          <Alert tone="warning" title={`${bot.name} is a search engine`}>
            Blocking or rate-limiting a verified search engine takes the site out of its results, so
            only an admin can do it, with their password and the engine’s name typed.
          </Alert>
          <div className="dialog-actions">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </Dialog>
    );
  }

  const rateValid =
    Number.isInteger(Number(perMinute)) &&
    Number(perMinute) >= 1 &&
    Number(perMinute) <= 6000 &&
    Number.isInteger(Number(burst)) &&
    Number(burst) >= 0 &&
    Number(burst) <= 1000;

  return (
    <ConfirmDialog
      title={TITLES[action]}
      description={
        action === 'allow'
          ? `${bot.name} is never blocked or rate-limited where this applies, whatever else a rule says.`
          : action === 'block'
            ? `Web servers answer ${bot.name} with 403. It stops honest bots only: one that lies about its name needs an IP ban.`
            : `Web servers answer ${bot.name} with 429 once an address goes over the rate.`
      }
      confirmLabel={TITLES[action].split(' a ')[0] ?? 'Save'}
      {...(reachesSearch ? { confirmPhrase: phrase } : {})}
      danger={action === 'block'}
      busy={save.isPending}
      error={error}
      onConfirm={() => {
        setError(null);
        if (action === 'rate_limit' && !rateValid) {
          setError('A rate from 1 to 6000 a minute, and a burst from 0 to 1000.');
          return;
        }
        save.mutate();
      }}
      onCancel={onClose}
    >
      <SelectField
        label="Applies to"
        value={scope}
        onChange={(event) => {
          setScope(event.target.value as Scope);
        }}
      >
        <option value="server">This server ({server.name})</option>
        {server.group !== null && <option value="group">Its group ({server.group.name})</option>}
        <option value="global">Every server</option>
      </SelectField>

      {action === 'rate_limit' && (
        <>
          <SelectField
            label="Which requests"
            value={match}
            onChange={(event) => {
              setMatch(event.target.value as 'bot' | 'category');
            }}
          >
            <option value="bot">{bot.name}</option>
            <option value="category">Every {CATEGORY_LABELS[bot.category].toLowerCase()}</option>
          </SelectField>
          <TextField
            label="Requests a minute, per address"
            type="number"
            min={1}
            max={6000}
            value={perMinute}
            onChange={(event) => {
              setPerMinute(event.target.value);
            }}
          />
          <TextField
            label="Burst"
            hint="Requests over the rate let through at once before refusing starts."
            type="number"
            min={0}
            max={1000}
            value={burst}
            onChange={(event) => {
              setBurst(event.target.value);
            }}
          />
        </>
      )}

      {reachesSearch && (
        <Alert tone="warning" title={`${bot.name} is a search engine`}>
          This can take the site out of its search results. It needs your password again, and the
          name typed below.
        </Alert>
      )}

      {action === 'block' && bot.respects_robots && (
        <Alert tone="info" title="The polite first step">
          {bot.operator} says {bot.name} obeys robots.txt. These lines ask it to stay away without
          blocking anything:
          <pre className="code-block">{`User-agent: ${bot.ua_patterns[0] ?? bot.name}\nDisallow: /`}</pre>
        </Alert>
      )}
    </ConfirmDialog>
  );
}

/**
 * Section 12's custom pattern: a user agent the catalog does not name, blocked by
 * a literal piece of it. Checked against `BotPattern` before it is sent, and — a
 * loose pattern like `bot` catches Googlebot too — treated as blocking a search
 * engine when it would catch one.
 */
export function PatternBlockDialog({
  server,
  onClose,
}: {
  server: BotRuleServer;
  onClose: () => void;
}): ReactNode {
  const session = useCurrentUser();
  const isAdmin = roleAtLeast(session.user.role, 'admin');
  const { ensureReauthenticated } = useReauth();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope>('server');
  const [pattern, setPattern] = useState('');
  const [error, setError] = useState<string | null>(null);

  const valid = BotPattern.safeParse(pattern.trim());
  const engines = valid.success
    ? botsTouched({ blocked_patterns: [valid.data] }, BOT_CATALOG).blocked.filter(
        isVerifiedSearchEngine,
      )
    : [];
  const reachesSearch = engines.length > 0;
  const ref: ConfigScopeRef =
    scope === 'global'
      ? { scope: 'global' }
      : scope === 'group' && server.group !== null
        ? { scope: 'group', id: server.group.id }
        : { scope: 'server', id: server.id };

  const save = useMutation({
    mutationFn: async (text: string) => {
      if (reachesSearch && !(await ensureReauthenticated())) {
        throw new Error('That needs your password again first.');
      }
      return changeWebRules({
        scope: ref,
        change: { kind: 'block_pattern', pattern: text },
        ...(reachesSearch ? { confirm: text } : {}),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['web-rules', server.id] });
      onClose();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not block it.');
    },
  });

  return (
    <ConfirmDialog
      title="Block a user agent"
      description="Web servers answer 403 to any user agent containing this text, in any case. It is matched literally, never as a pattern."
      confirmLabel="Block"
      {...(reachesSearch && valid.success ? { confirmPhrase: valid.data } : {})}
      danger
      busy={save.isPending}
      error={error}
      onConfirm={() => {
        setError(null);
        if (!valid.success) {
          setError(valid.error.issues[0]?.message ?? 'Not a pattern Bartizo can write');
          return;
        }
        if (reachesSearch && !isAdmin) {
          setError('Only an admin can block a verified search engine.');
          return;
        }
        save.mutate(valid.data);
      }}
      onCancel={onClose}
    >
      <TextField
        label="User agent contains"
        hint="3 to 100 letters, digits, spaces and . _ - / ( ) + :"
        autoComplete="off"
        spellCheck={false}
        value={pattern}
        onChange={(event) => {
          setPattern(event.target.value);
        }}
      />
      <SelectField
        label="Applies to"
        value={scope}
        onChange={(event) => {
          setScope(event.target.value as Scope);
        }}
      >
        <option value="server">This server ({server.name})</option>
        {server.group !== null && <option value="group">Its group ({server.group.name})</option>}
        <option value="global">Every server</option>
      </SelectField>
      {reachesSearch && (
        <Alert tone="warning" title="This catches a search engine">
          {engines.map((bot) => bot.name).join(', ')} would be blocked too. Only an admin can do
          that, with their password and the text typed again.
        </Alert>
      )}
    </ConfirmDialog>
  );
}
